// Initialize Sentry before anything else so module-evaluation errors in later
// imports are captured.
import "@/lib/observability";

import React, { Suspense, lazy, useEffect } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { ActivityRecorder } from "@/components/activity-recorder";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { toast } from "sonner";

import {
  RequireAuth,
  RequireCapability,
  RequireCompanyAdmin,
  RequirePlatformAdmin,
} from "@/components/auth-boundary";
import { AppErrorBoundary } from "@/components/error-boundary";
import { PageLoader } from "@/components/ui/spinner";
import { Toaster } from "@/components/ui/sonner";
import { ApiError } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { scorecardSummaryRoute } from "@/lib/scorecard-summary";
import { reportError, setSentryUser } from "@/lib/observability";
import LoginPage from "@/routes/login";
import ForgotPasswordPage from "@/routes/forgot-password";
import ResetPasswordPage from "@/routes/reset-password";
import VerifyEmailPage from "@/routes/verify-email";
import MarketingLayout from "@/marketing/marketing-layout";

import "./styles.css";

type RouteModule = { default: React.ComponentType<unknown> };

function isDynamicImportError(error: unknown) {
  if (!(error instanceof Error)) return false;
  return /ChunkLoadError|Loading chunk|dynamically imported module|module script/i.test(
    error.message
  );
}

// Stale tabs request old hashed chunks after a redeploy; a reload fetches the
// new index.html and heals them. Returns false if this release already got its
// one reload — the chunk is failing on fresh HTML, which is a real error.
function reloadForNewRelease() {
  const release = import.meta.env.VITE_RELEASE || "dev";
  const key = `shift:chunk-reload:${release}`;
  try {
    if (sessionStorage.getItem(key) === "1") return false;
    sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage unavailable — reload without loop protection.
  }
  window.location.reload();
  return true;
}

window.addEventListener("vite:preloadError", (event) => {
  if (reloadForNewRelease()) event.preventDefault();
});

function lazyRoute<T extends RouteModule>(load: () => Promise<T>) {
  return lazy(async () => {
    try {
      const module = await load();
      // vite:preloadError below suppresses the error to reload instead, which
      // resolves the import to undefined; without this, React renders the
      // error boundary (and reports to Sentry) before the reload lands.
      if (!module) return new Promise<T>(() => {});
      return module;
    } catch (error) {
      if (isDynamicImportError(error) && reloadForNewRelease()) {
        // The page is reloading; keep the Suspense fallback up and stay out
        // of Sentry.
        return new Promise<T>(() => {});
      }
      throw error;
    }
  });
}

// Public marketing pages — split away from the authenticated platform bundle.
const MarketingHomePage = lazyRoute(() => import("@/marketing/pages/home"));
const MarketingEnginePage = lazyRoute(() => import("@/marketing/pages/engine"));
const MarketingCeosPage = lazyRoute(() => import("@/marketing/pages/ceos"));
const MarketingPricingPage = lazyRoute(() => import("@/marketing/pages/pricing"));
const MarketingFaqPage = lazyRoute(() => import("@/marketing/pages/faq"));
const MarketingNotFoundPage = lazyRoute(
  () => import("@/marketing/pages/not-found"),
);

// Member pages — lazy so the auth bundle stays minimal for sign-in.
const DashboardPage = lazyRoute(() => import("@/routes/dashboard"));
const CoachPage = lazyRoute(() => import("@/routes/coach"));
const ScorecardPage = lazyRoute(() => import("@/routes/scorecard"));
const ScorecardSummaryPage = lazyRoute(() => import("@/routes/scorecard-summary"));
const ScorecardPreviousPage = lazyRoute(() => import("@/routes/scorecard-previous"));
const SettingsPage = lazyRoute(() => import("@/routes/settings"));
const StagingPage = lazyRoute(() => import("@/routes/staging"));
const WebinarDecksPage = lazyRoute(() => import("@/routes/webinar-decks"));
const PromptPackDetailPage = lazyRoute(() => import("@/routes/prompt-pack-detail"));
const PromptLibraryPage = lazyRoute(() => import("@/routes/prompt-library"));
const VideosPage = lazyRoute(() => import("@/routes/videos"));
const VideoDetailPage = lazyRoute(() => import("@/routes/video-detail"));
const PlaybooksPage = lazyRoute(() => import("@/routes/playbooks"));
const PlaybookDetailPage = lazyRoute(() => import("@/routes/playbook-detail"));

// Admin pages — split into a separate chunk so members never download them.
const AdminUsersPage = lazyRoute(() => import("@/routes/admin-users"));
const AdminUserDetailPage = lazyRoute(() => import("@/routes/admin-user-detail"));
const ParticipantProfilePage = lazyRoute(() => import("@/routes/participant-profile"));
const AdminOrganizationPage = lazyRoute(() => import("@/routes/admin-organization"));
const AdminReportsPage = lazyRoute(() => import("@/routes/admin-reports"));
const AdminCopilotUsagePage = lazyRoute(
  () => import("@/routes/admin-copilot-usage"),
);
const AdminEmailLabPage = lazyRoute(() => import("@/routes/admin-email-lab"));
const AdminEngagementPage = lazyRoute(() => import("@/routes/admin-engagement"));
const LearningInsightsPage = lazyRoute(
  () => import("@/routes/learning-insights"),
);
const AdminMeetingsPage = lazyRoute(() => import("@/routes/admin-meetings"));
const AdminContentPage = lazyRoute(() => import("@/routes/admin-content"));
const AdminBuilderPage = lazyRoute(() => import("@/routes/admin-builder"));
const AdminSchedulePage = lazyRoute(() => import("@/routes/admin-schedule"));

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // Pages render their own error states; just report the unexpected.
    onError: (error, query) => {
      reportError(error, { queryKey: query.queryKey });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportError(error, { mutationKey: mutation.options.mutationKey });
      // Toast only when the mutation has no local onError — local handlers
      // already surface the failure, and this handler runs in addition.
      if (!mutation.options.onError) {
        toast.error(
          error instanceof ApiError && error.status < 500
            ? error.message
            : "Something went wrong. Please try again."
        );
      }
    },
  }),
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
});

function PageFallback() {
  return <PageLoader className="min-h-screen" />;
}

function PromptLibraryRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/prompt-library${search}`} replace />;
}

function LegacyTutorialRedirect() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/playbooks/${slug}` : "/playbooks"} replace />;
}

function SentryUserContext() {
  const session = useSession();
  const userId = session.data?.user?.id ?? null;

  useEffect(() => {
    if (session.isSuccess) setSentryUser(userId);
  }, [session.isSuccess, userId]);

  return null;
}

function RootRoute() {
  return (
    <MarketingLayout>
      <MarketingHomePage />
    </MarketingLayout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SentryUserContext />
      <BrowserRouter>
        <AppErrorBoundary>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route element={<MarketingLayout />}>
                <Route path="/engine" element={<MarketingEnginePage />} />
                <Route path="/ceos" element={<MarketingCeosPage />} />
                <Route path="/pricing" element={<MarketingPricingPage />} />
                <Route path="/faq" element={<MarketingFaqPage />} />
              </Route>
              <Route path="/login" element={<LoginPage />} />
              {/* No self-serve signup: orgs provision members, who set a password
                  via the emailed link (/auth/reset-password/:key). */}
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password/:key" element={<ResetPasswordPage />} />
              <Route path="/auth/verify-email/:key" element={<VerifyEmailPage />} />
              <Route
                path="/staging"
                element={
                  <RequireAuth>
                    <StagingPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <DashboardPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/scorecard"
                element={
                  <RequireAuth>
                    <ScorecardPage />
                  </RequireAuth>
                }
              />
              <Route
                path={scorecardSummaryRoute}
                element={
                  <RequireAuth>
                    <ScorecardSummaryPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/coach"
                element={
                  <RequireAuth>
                    <CoachPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/scorecard/previous"
                element={
                  <RequireAuth>
                    <ScorecardPreviousPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/webinar-decks"
                element={
                  <RequireAuth>
                    <WebinarDecksPage />
                  </RequireAuth>
                }
              />
              <Route path="/prompt-packs" element={<PromptLibraryRedirect />} />
              <Route
                path="/prompt-library"
                element={
                  <RequireAuth>
                    <PromptLibraryPage />
                  </RequireAuth>
                }
              />
              {/* Signs in like any member page, unless the pack is shared by link. */}
              <Route path="/prompt-packs/:slug" element={<PromptPackDetailPage />} />
              <Route
                path="/videos"
                element={
                  <RequireAuth>
                    <VideosPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/videos/:videoId"
                element={
                  <RequireAuth>
                    <VideoDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/playbooks"
                element={
                  <RequireAuth>
                    <PlaybooksPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/playbooks/:slug"
                element={
                  <RequireAuth>
                    <PlaybookDetailPage />
                  </RequireAuth>
                }
              />
              <Route path="/tutorials" element={<LegacyTutorialRedirect />} />
              <Route
                path="/tutorials/:slug"
                element={<LegacyTutorialRedirect />}
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <SettingsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/schedule"
                element={
                  <RequireCompanyAdmin>
                    <AdminSchedulePage />
                  </RequireCompanyAdmin>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireCapability capability="team_members.read">
                    <AdminUsersPage />
                  </RequireCapability>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <RequireCapability capability="team_members.read">
                    <AdminUserDetailPage />
                  </RequireCapability>
                }
              />
              <Route
                path="/participants/:userId"
                element={
                  <RequireCapability capability="team_members.read">
                    <ParticipantProfilePage />
                  </RequireCapability>
                }
              />
              <Route
                path="/admin/organizations"
                element={
                  <RequireCompanyAdmin>
                    <AdminOrganizationPage />
                  </RequireCompanyAdmin>
                }
              />
              <Route
                path="/admin/organizations/:orgId"
                element={
                  <RequireCompanyAdmin>
                    <AdminOrganizationPage />
                  </RequireCompanyAdmin>
                }
              />
              {/* Legacy singular path — keep bookmarks/links working. */}
              <Route
                path="/admin/organization"
                element={<Navigate to="/admin/organizations" replace />}
              />
              <Route
                path="/admin/reports"
                element={
                  <RequireCompanyAdmin>
                    <AdminReportsPage />
                  </RequireCompanyAdmin>
                }
              />
              <Route
                path="/admin/engagement"
                element={
                  <RequirePlatformAdmin>
                    <AdminEngagementPage />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="/admin/copilot"
                element={
                  <RequirePlatformAdmin>
                    <AdminCopilotUsagePage />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="/admin/email-lab"
                element={
                  <RequirePlatformAdmin>
                    <AdminEmailLabPage />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="/admin/learning"
                element={
                  <RequireCompanyAdmin>
                    <LearningInsightsPage />
                  </RequireCompanyAdmin>
                }
              />
              <Route
                path="/admin/meetings"
                element={
                  <RequirePlatformAdmin>
                    <AdminMeetingsPage />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="/admin/content"
                element={
                  <RequireCompanyAdmin>
                    <AdminContentPage />
                  </RequireCompanyAdmin>
                }
              />
              <Route
                path="/admin/builder"
                element={
                  <RequirePlatformAdmin>
                    <AdminBuilderPage />
                  </RequirePlatformAdmin>
                }
              />
              <Route element={<MarketingLayout />}>
                <Route path="*" element={<MarketingNotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </AppErrorBoundary>
        <Toaster richColors position="top-right" />
        <ActivityRecorder />
        <Analytics />
        <SpeedInsights />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

ReactDOM.createRoot(document.getElementById("app")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
